import { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { IoAdd, IoSearchOutline } from "react-icons/io5";
import { AllBanksModal } from "../../components/modals/AllBanksModal";
import { AddBankModal } from "../../components/modals/AddBankModal";
import BVNVerificationModal from "../../components/modals/BVNVerificationModal";
import SelfEmployed from "../../components/modals/SelfEmployed";
import Employed from "../../components/modals/Employed";
import Student from "../../components/modals/Student";
import Pagination from "../../components/Pagination";
import { FaPhone, FaWhatsapp } from "react-icons/fa6";
import UpdateCreditLimit from "../../components/modals/UpdateCreditLimit";
import StateUsers from "../../components/users/StateUsers";
import { getFlexibleUsersPaginated } from "../../redux/thunk";
import LoadingSpinner from "../../components/elements/LoadingSpinner";
import OutrightCustomers from "../../components/users/OutrightCustomers";

// Helper function to display business owner status
const formatBusinessOwner = (
  isBusinessOwner: boolean | undefined | null
): string => {
  // Handle cases where the field might not exist in older user records
  if (isBusinessOwner === undefined || isBusinessOwner === null) {
    return "Not specified";
  }
  return isBusinessOwner ? "Yes" : "No";
};

const UserDetails = () => {
  const [bvnModalOpen, setBvnModalOpen] = useState(false);
  const [studentModalOpen, setStudentModalOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [selectedBvn, setSelectedBvn] = useState("");
  const [selectedInfo, setSelectedInfo] = useState<any>({});
  const [activeTab, setActiveTab] = useState("flexible");
  const [selfEmployedModalOpen, setSelfEmployedModalOpen] = useState(false);
  const [allBanksModal, setAllBanksModal] = useState(false);
  const [allBanksDetails, setAllBanksDetails] = useState([]);
  const [employedModalOpen, setEmployedModalOpen] = useState(false);
  const [userId, setUserId] = useState("");
  const [addBankModal, setAddBankModal] = useState(false);
  const [userToAddBank, setUserToAddBank] = useState({});
  const [searchedUser, setSearchedUser] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [userData, setUserData] = useState([]);
  const [viewUpdateCreditLimit, setViewUpdateCreditLimit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [flexibleUsers, setFlexibleUsers] = useState([]);
  const [searchActive, setSearchActive] = useState(false);
  const [unmountModal, setUnmountModal] = useState(false);
  const [searchPagination, setSearchPagination] = useState({ total: 0, totalPages: 0 });

  const dispatch = useAppDispatch();
  const { stateUsers } = useAppSelector((store) => store.users);

  const [itemsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const indexOfLastPost = currentPage * itemsPerPage;
  const indexOfFirstPost = indexOfLastPost - itemsPerPage;
  const data = searchActive ? searchResults : flexibleUsers;

  const callFlexFunc = useCallback(() => {
    dispatch(
      getFlexibleUsersPaginated({
        page: currentPage,
        limit: itemsPerPage,
      })
    ).then((response: any) => {
      const res = response.payload;
      setFlexibleUsers(res?.data);
      setTotalPages(res?.pagination?.totalPages);
      setTotalResults(res?.pagination?.total);
      setUnmountModal(false);
      setIsLoading(false);
    });
  }, [currentPage, itemsPerPage, dispatch, unmountModal]);

  useEffect(() => {
    setIsLoading(true);
    callFlexFunc();
  }, [callFlexFunc]);

  useEffect(() => {
    setSearchActive(false);
    setSearchResults([]);
    setSearchPagination({ total: 0, totalPages: 0 });
  }, []);

  // Pagination
  const goToPage = async (pageNumber: any) => {
    setSearchedUser("");
    setCurrentPage(pageNumber);
  };

  const prevPage = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1);
    }
  };

  const handleBvnClick = (bvn: string) => {
    setSelectedBvn(bvn);
    setBvnModalOpen(true);
  };

  const handleViewAllBank = (id: string, linkedBanks: any) => {
    setUserId(id);
    setAllBanksDetails(linkedBanks);
    setAllBanksModal((prev) => !prev);
  };

  const handleUpdateCreditLimit = (data: any) => {
    setUserData(data);
    setViewUpdateCreditLimit((prev) => !prev);
  };

  const handleJobClick = (details: string, occupation: any) => {
    setSelectedInfo(details);
    if (occupation === "Self-Employed") {
      setSelfEmployedModalOpen(true);
    } else if (occupation === "Employed") {
      setEmployedModalOpen(true);
    } else {
      setStudentModalOpen(true);
    }
  };

  

  const handleAddBank = (firstName: any, lastName: any, id: string) => {
    setUserToAddBank({ name: lastName + " " + firstName, id });
    setAddBankModal((prev) => !prev);
  };

  const searchForUsers = (value: string) => {
    setSearchedUser(value);
    if (value.trim().length > 0) {
      setSearchActive(true);
      setIsLoading(true);
      setCurrentPage(1);
      dispatch(
        getFlexibleUsersPaginated({ page: 1, limit: itemsPerPage, search: value.trim() })
      ).then((response: any) => {
        const res = response.payload;
        setSearchResults(res?.data || []);
        setSearchPagination({ total: res?.pagination?.total || 0, totalPages: res?.pagination?.totalPages || 0 });
        setIsLoading(false);
      });
    } else {
      setSearchActive(false);
      setSearchResults([]);
      setSearchPagination({ total: 0, totalPages: 0 });
    }
  };

  return (
    <main className="overflow-x-auto w-full">
      <div className="flex gap-10 mb-6">
        <span
          className={`cursor-pointer pb-2 ${
            activeTab === "flexible" ? "border-b-2 border-green-500" : "null"
          }`}
          onClick={() => setActiveTab("flexible")}
        >
          Flexible Customers
        </span>
        <span
          className={`cursor-pointer pb-2 ${
            activeTab === "outright" ? "border-b-2 border-green-500" : "null"
          }`}
          onClick={() => setActiveTab("outright")}
        >
          Outright Customers
        </span>
        <span
          className={`cursor-pointer pb-2 ${
            activeTab === "stateUsers" ? "border-b-2 border-green-500" : "null"
          }`}
          onClick={() => setActiveTab("stateUsers")}
        >
          State Users
        </span>
      </div>
      {activeTab === "flexible" ? (
        <div className="bg-white rounded-md shadow-md pb-6">
          <div className={`w-full overflow-x-auto`}>
            <div
              className="flex items-center justify-between p-6"
              style={{ minWidth: "700px" }}
            >
              <div className="flex justify-between w-full">
                <h1 className="text-base font-semibold ">User Details</h1>
                <div className="relative md:w-[30rem] w-fit">
                  <IoSearchOutline className="w-6 h-6 absolute top-[0.6rem] left-2 text-gray-300" />
                  <input
                    type="search"
                    name="searchedUser"
                    id="searchedUser"
                    value={searchedUser}
                    onChange={(e) => searchForUsers(e.target.value)}
                    placeholder="Search with email or phone number"
                    className="border p-2 text-sm rounded-md indent-7 w-full"
                  />
                </div>
                <label htmlFor="filter">
                  Sort:
                  <select
                    name="filter"
                    id="filter"
                    className="border p-2 ml-4"
                    onChange={(e) => setFilter(e.target.value)}
                  >
                    <option value="all">All</option>
                    <option value="employed">Employed</option>
                    <option value="self-employed">Self-employed</option>
                    <option value="student">Students</option>
                  </select>
                </label>
              </div>
            </div>
            <section className="overflow-x-auto">
              <table className="w-[1200px]" style={{ minWidth: "700px" }}>
                <thead className="bg-gray-50 font-bold p-4 text-left">
                  {filter === "student" && (
                    <tr>
                      <th className="p-2">S/N</th>
                      <th className="p-2 text-nowrap">Name</th>
                      <th className="p-2 text-nowrap">Email</th>
                      <th className="p-2 text-nowrap">Phone Number</th>
                      <th className="p-2 text-nowrap">Contact</th>
                      <th className="p-2 text-nowrap">Add Bank</th>
                      <th className="p-2 text-nowrap">All Banks</th>
                      <th className="p-2 text-nowrap">Business Owner</th>
                      <th className="p-2 text-nowrap">Credit Score(Balance)</th>
                      <th className="p-2 text-nowrap">Credit Score</th>
                      <th className="p-2 text-nowrap">Action</th>
                    </tr>
                  )}
                  {filter === "employed" && (
                    <tr>
                      <th className="p-2 ">S/N</th>
                      <th className="p-2 text-nowrap">Name</th>
                      <th className="p-2 text-nowrap">Email</th>
                      <th className="p-2 text-nowrap">Phone Number</th>
                      <th className="p-2 text-nowrap">Contact</th>
                      <th className="p-2 text-nowrap">Add Bank</th>
                      <th className="p-2 text-nowrap">All Banks</th>
                      <th className="p-2 text-nowrap">Business Owner</th>
                      <th className="p-2 text-nowrap">Credit Score(Balance)</th>
                      <th className="p-2 text-nowrap">Credit Score</th>
                      <th className="p-2 text-nowrap">Action</th>
                    </tr>
                  )}
                  {filter === "all" && (
                    <tr>
                      <th className="p-2 ">S/N</th>
                      <th className="p-2 text-nowrap">Name</th>
                      <th className="p-2 text-nowrap">Email</th>
                      <th className="p-2 text-nowrap">Phone Number</th>
                      <th className="p-2 text-nowrap">Contact</th>
                      <th className="p-2 text-nowrap">Add Bank</th>
                      <th className="p-2 text-nowrap">All Banks</th>
                      <th className="p-2 text-nowrap">Business Owner</th>
                      <th className="p-2 text-nowrap">Credit Score(Balance)</th>
                      <th className="p-2 text-nowrap">Credit Score</th>
                      <th className="p-2 text-nowrap">Action</th>
                    </tr>
                  )}
                  {filter === "self-employed" && (
                    <tr>
                      <th className="p-2 ">S/N</th>
                      <th className="p-2 text-nowrap">Name</th>
                      <th className="p-2 text-nowrap">Email</th>
                      <th className="p-2 text-nowrap">Phone Number</th>
                      <th className="p-2 text-nowrap">Contact</th>
                      <th className="p-2 text-nowrap">Add Bank</th>
                      <th className="p-2 text-nowrap">All Banks</th>
                      <th className="p-2 text-nowrap">Business Owner</th>
                      <th className="p-2 text-nowrap">Credit Score(Balance)</th>
                      <th className="p-2 text-nowrap">Credit Score</th>
                      <th className="p-2 text-nowrap">Action</th>
                    </tr>
                  )}
                </thead>
                <tbody className="px-4">
                  {isLoading ? (
                    <tr className="text-center">
                      <td colSpan={15} className="p-3">
                        <LoadingSpinner />
                      </td>
                    </tr>
                  ) : data && data.length > 0 && activeTab === "flexible" ? (
                    data
                      .filter((customer: any) => {
                        if (filter === "employed") {
                          return customer.employmentStatus === "employed";
                        } else if (filter === "self-employed") {
                          return customer.employmentStatus === "selfEmployed";
                        } else if (filter === "student") {
                          return customer.employmentStatus === "student";
                        } else if (filter === "all") {
                          return customer.employmentStatus;
                        }
                        return true;
                      })
                      .map((data: any, index) => (
                        <tr
                          className="border-b border-gray-300 py-2"
                          key={index}
                        >
                          <td className="text-secondary p-2">
                            {index + indexOfFirstPost + 1}
                          </td>
                          <td className="p-2 text-nowrap capitalize">
                            {data.lastName.toLowerCase()}{" "}
                            {data.firstName.toLowerCase()}
                          </td>
                          <td className="p-2">{data.email}</td>
                          <td className="p-2 text-nowrap">
                            {data?.phoneNumber || "No number"}
                          </td>
                          <td className="p-2">
                            <button
                              className="mr-3"
                              type="button"
                              title="call customer"
                            >
                              <a href={`tel:${data?.phoneNumber}`}>
                                <FaPhone />
                              </a>
                            </button>
                            <button type="button" title="text via whatsapp">
                              <a
                                href={`https://wa.me/${data?.phoneNumber?.replace(
                                  /^0/,
                                  "234"
                                )}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <FaWhatsapp />
                              </a>
                            </button>
                          </td>
                          <td className="p-2 text-center">
                            <button
                              className="text-secondary border"
                              onClick={() =>
                                handleAddBank(
                                  data.firstName,
                                  data.lastName,
                                  data._id
                                )
                              }
                            >
                              <IoAdd className="w-9 h-9" />
                            </button>
                          </td>
                          <td className="p-2 text-center">
                            <button
                              className="bg-secondary text-white text-sm rounded-md p-2"
                              onClick={() =>
                                handleViewAllBank(data._id, data.linkedBanks)
                              }
                            >
                              View
                            </button>
                          </td>
                          <td className="p-2 text-nowrap">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                formatBusinessOwner(data.isBusinessOwner) ===
                                "Yes"
                                  ? "bg-green-100 text-green-800"
                                  : formatBusinessOwner(
                                      data.isBusinessOwner
                                    ) === "No"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {formatBusinessOwner(data.isBusinessOwner)}
                            </span>
                          </td>
                          <td className="p-2">
                            ₦
                            {data.creditScore < 0
                              ? "0"
                              : data.creditScore.toLocaleString()}
                          </td>
                          <td className="p-2">
                            ₦{data.creditLimit.toLocaleString()}
                          </td>
                          <td className="p-2 text-nowrap">
                            <button
                              type="button"
                              className="p-2 text-sm text-secondary border border-secondary"
                              onClick={() => handleUpdateCreditLimit(data)}
                            >
                              Update Credit Limit
                            </button>
                          </td>
                        </tr>
                      ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="p-2 text-center">
                        No users found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>
          </div>
          <section className="p-3 my-5">
            <Pagination
              length={searchActive ? searchPagination.total : totalResults}
              itemsPerPage={itemsPerPage}
              handlePagination={(page: number) => {
                if (searchActive) {
                  setIsLoading(true);
                  setCurrentPage(page);
                  dispatch(
                    getFlexibleUsersPaginated({ page, limit: itemsPerPage, search: searchedUser.trim() })
                  ).then((response: any) => {
                    const res = response.payload;
                    setSearchResults(res?.data || []);
                    setSearchPagination({ total: res?.pagination?.total || 0, totalPages: res?.pagination?.totalPages || 0 });
                    setIsLoading(false);
                  });
                } else {
                  goToPage(page);
                }
              }}
              currentPage={currentPage}
              prevPage={prevPage}
              nextPage={nextPage}
              responseTotalPages={searchActive ? searchPagination.totalPages : totalPages}
            />
          </section>
        </div>
      ) : activeTab === "stateUsers" ? (
        <StateUsers
          stateUsers={stateUsers.slice().reverse()}
          handleBvnClick={handleBvnClick}
          handleViewAllBank={handleViewAllBank}
          handleAddBank={handleAddBank}
          handleUpdateCreditLimit={handleUpdateCreditLimit}
          page={"userDetails"}
          unmountModal={unmountModal}
          setUnmountModal={setUnmountModal}
        />
      ) : (
        <OutrightCustomers />
      )}

      {viewUpdateCreditLimit && (
        <UpdateCreditLimit
          userData={userData}
          closeModal={() => {
            setViewUpdateCreditLimit(false);
            setUnmountModal(true);
          }}
        />
      )}

      {allBanksModal && (
        <AllBanksModal
          setAllBanksModal={setAllBanksModal}
          setAllBanksDetails={setAllBanksDetails}
          allBanksDetails={allBanksDetails}
          setSearchedUser={setSearchedUser}
          userId={userId}
          setUnmountModal={setUnmountModal}
        />
      )}

      {addBankModal && (
        <AddBankModal
          setAddBankModal={setAddBankModal}
          userToAddBank={userToAddBank}
          setUnmountModal={setUnmountModal}
        />
      )}

      {bvnModalOpen && (
        <BVNVerificationModal
          setBvnModalOpen={setBvnModalOpen}
          selectedBvn={selectedBvn}
        />
      )}

      {selfEmployedModalOpen && (
        <SelfEmployed
          setSelfEmployedModalOpen={setSelfEmployedModalOpen}
          selectedInfo={selectedInfo}
        />
      )}
      {employedModalOpen && (
        <Employed
          setEmployedModalOpen={setEmployedModalOpen}
          selectedInfo={selectedInfo}
        />
      )}
      {studentModalOpen && (
        <Student
          selectedInfo={selectedInfo}
          setStudentModalOpen={setStudentModalOpen}
        />
      )}
    </main>
  );
};

export default UserDetails;
